import { Injectable, inject } from '@angular/core';
import { DialogService } from 'primeng/dynamicdialog';
import { BehaviorSubject, catchError, combineLatest, debounceTime, distinctUntilChanged, filter, Observable, of, Subject, take, tap } from 'rxjs';
import { AusModel, DocumentSummaryModel, InvalidControlModel, QuickActionDropdownType } from '../../cmg-1003/models';
import { BorrowerModel } from '../../cmg-1003/models/borrower.model';
import { CreditSummaryModel } from '../../cmg-1003/models/credit-summary.model';
import { EmploymentIncomeExpenseModel } from '../../cmg-1003/models/employment-income-expense.model';
import { LoanDataModel } from '../../cmg-1003/models/loan-information.model';
import { PropertyInformationModel } from '../../cmg-1003/models/property-information.model';
import { Cmg1003Service } from '../../cmg-1003/services/cmg-1003.service';
import { FeatureFlagRepository, FeatureName, SessionQuery, SessionStore } from '../../core/state';
import { LoanApplicationModel } from '../../disclosure/models/loan-application.model';
import { LoanApplicationQuery } from '../../disclosure/state/loan-application/loan-application.query';
import { IrsForm4506, IrsForm4506LoanModel } from '../../form-4506c/form-4506c.models';
import { Form4506CService } from '../../form-4506c/form-4506c.service';
import {
  AusQuery,
  Cmg1003AssetsQuery,
  Cmg1003BorrowerQuery,
  Cmg1003CreditSummaryQuery,
  Cmg1003DocumentSummaryQuery,
  Cmg1003EmploymentIncomeExpensesQuery,
  Cmg1003FileDataQuery,
  Cmg1003LoanQuery,
  Cmg1003LookupsQuery,
  Cmg1003PropertyQuery,
  Cmg1003StoreQueryService,
  Cmg1003TransactionQuery,
} from '../../loan-state';
import { UnsavedRefreshConfirmationComponent } from '../components/unsaved-refresh-confirmation/unsaved-refresh-confirmation.component';
import { RetailBridgeService } from '../../shared/services/retail-bridge.service';
import { GetAusStatus } from '../models/aus-status.models';
import { GetCreditStatus } from '../models/credit.models';
import { GetDisclosuresStatus } from '../models/disclosures-status.model';
import { GetDocsStatus } from '../models/documents.models';
import { GetForm4506CStatus } from '../models/form-4506-c.model';
import { ILoanStatusCard, LoanStatusCardConfig, LoanStatusCardType } from '../models/loan-status-card.model';
import { GetPricingStatus } from '../models/pricing.models';
import {
  getRESPACompleteFields,
  getRESPAStatus,
  isFhaRefiStreamlineVaRefiIRRRL,
  respaFields,
  respaRequiredFieldCount,
  RespaStatusType,
} from '../models/respa.models';
import { CmgLoanBannerDetailsStore } from '../state/loan-banner-details.store';
import { CmgLoanBannerStatusCardStore } from '../state/loan-banner-status-card.store';
import { DisclosureDates } from '../../disclosure/models/disclosure-dates.model';
import { DisclosureDatesRepository } from '../../disclosure/state/disclosure-dates/disclosure-dates.repository';
import { LoanApplicationService } from '../../disclosure/services/loan-application.service';
import { UrlaSaveHistoryModalComponent } from '../components/urla-save-history-modal/urla-save-history-modal.component';
import { SecurityProfileModel, SecurityProfileStore } from '../../loan-state/security-profile';
import { AppSettingsService } from '../../shared';
import { IdValue } from '../../shared/models';

@Injectable({ providedIn: 'root' })
export class LoanBannerService {
  private readonly _loanBannerDetailsStore = inject(CmgLoanBannerDetailsStore);
  private readonly _loanBannerStatusCardStore = inject(CmgLoanBannerStatusCardStore);
  private readonly _sessionQuery = inject(SessionQuery);
  private readonly _sessionStore = inject(SessionStore);
  private readonly _storeQueryService = inject(Cmg1003StoreQueryService);
  private readonly _retailBridgeService = inject(RetailBridgeService);
  private readonly _fileDataQuery = inject(Cmg1003FileDataQuery);
  private readonly _loanInfoQuery = inject(Cmg1003LoanQuery);
  private readonly _transactionInfoQuery = inject(Cmg1003TransactionQuery);
  private readonly _lookups = inject(Cmg1003LookupsQuery);
  private readonly _borrowerQuery = inject(Cmg1003BorrowerQuery);
  private readonly _employentIncomeQuery = inject(Cmg1003EmploymentIncomeExpensesQuery);
  private readonly _assetsQuery = inject(Cmg1003AssetsQuery);
  private readonly _property = inject(Cmg1003PropertyQuery);
  private readonly _documentSummaryQuery = inject(Cmg1003DocumentSummaryQuery);
  private readonly _creditSummaryQuery = inject(Cmg1003CreditSummaryQuery);
  private readonly _ausQuery = inject(AusQuery);
  private readonly _disclosuresLoanQuery = inject(LoanApplicationQuery);
  private readonly _cmg1003Service = inject(Cmg1003Service);
  private readonly _dialogService = inject(DialogService);
  private readonly _form4506cService = inject(Form4506CService);
  private readonly _disclosureDatesRepository = inject(DisclosureDatesRepository);
  private readonly _loanApplicationService = inject(LoanApplicationService);
  private readonly _featureFlagsRepository = inject(FeatureFlagRepository);
  private readonly _securityProfileStore = inject(SecurityProfileStore);
  private readonly _appSettingsService = inject(AppSettingsService);

  private readonly _loanCardSelection$: Subject<ILoanStatusCard> = new Subject();
  loanCardSelection$: Observable<ILoanStatusCard> = this._loanCardSelection$.asObservable();

  private readonly _historyButtonClicked$: Subject<boolean> = new Subject();
  historyButtonClicked$: Observable<boolean> = this._historyButtonClicked$.asObservable();

  private readonly _resetButtonClicked$: Subject<boolean> = new Subject();
  resetButtonClicked$: Observable<boolean> = this._resetButtonClicked$.asObservable();

  private readonly _refreshButtonClicked$: Subject<boolean> = new Subject();
  refreshButtonClicked$: Observable<boolean> = this._refreshButtonClicked$.asObservable();

  private readonly _saveButtonClicked$: Subject<boolean> = new Subject();
  saveButtonClicked$: Observable<boolean> = this._saveButtonClicked$.asObservable();

  private readonly _slideOutPanel$: Subject<string> = new Subject();
  slideOutPanel$: Observable<string> = this._slideOutPanel$.asObservable();

  private readonly _respaNoAppDateStatus$: Subject<RespaStatusType> = new BehaviorSubject(RespaStatusType.NotCompleted);
  respaNoAppDateStatus$: Observable<RespaStatusType> = this._respaNoAppDateStatus$.asObservable();

  tridCompleteFields$ = new BehaviorSubject<string[]>([]);
  isFhaStreamlineOrVaIrrrl$ = new BehaviorSubject<boolean>(false);

  disableSave$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  isNewItemBeingAdded$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  private readonly _fileData$ = this._fileDataQuery.select();
  private readonly _loanInfo$ = this._loanInfoQuery.select();
  private readonly _loanStatus$ = this._sessionQuery.select((s) => s.loanStatusId);
  private readonly _transactionInfo$ = this._transactionInfoQuery.select();
  private readonly _borrowers$ = this._borrowerQuery.selectAll();
  private readonly _income$ = this._employentIncomeQuery.selectAll();
  private readonly _totalNriFromReo$ = this._assetsQuery.getTotalNetRentalIncome$();
  private readonly _property$ = this._property.select();
  private readonly _lookups$ = this._lookups.select();
  private readonly _documentSummary$ = this._documentSummaryQuery.select();
  private readonly _creditSummary$ = this._creditSummaryQuery.select();
  private readonly _aus$ = this._ausQuery.select();
  private readonly _disclosures$ = this._disclosuresLoanQuery.select();
  private readonly _disclosuresApplicationDate$ = this._disclosuresLoanQuery.select((state) => [state.applicationDate, state.fileDataId]);
  private readonly _validations$ = this._cmg1003Service.validations$;
  private readonly _disclosureDates$ = this._disclosureDatesRepository.dates$;
  private _activeProfile: SecurityProfileModel;

  constructor() {
    this._initLoanBannerData();
  }

  selectCard(card: ILoanStatusCard) {
    // _loanBannerStatusCardStore.setActive will be called in Urla2Component
    this._loanCardSelection$.next(card);
  }

  openRefreshDialog(): void {
    const dialogRef = this._dialogService.open(UnsavedRefreshConfirmationComponent, {
      draggable: true,
      keepInViewport: true,
    });
    dialogRef.onClose.subscribe((refresh: boolean) => {
      if (refresh) {
        if (this._storeQueryService.isStateDirty()) {
          this._sessionStore.update({ cached1003: null });
          this._cmg1003Service.autoRecoveryDelete1003(true, () => {
            window.location.reload();
          });
        } else {
          window.location.reload();
        }
      }
    });
  }

  openUrlaSaveHistoryModal({ skipToReportAnIssue = false }: { skipToReportAnIssue?: boolean } = {}): void {
    this._dialogService.open(UrlaSaveHistoryModalComponent, {
      showHeader: false,
      styleClass: 'urla-save-history-modal',
      width: '700px',
      data: {
        skipToReportAnIssue,
      },
    });
  }

  updateHistoryButtonClicked(val: boolean): void {
    this._historyButtonClicked$.next(val);
    this.openUrlaSaveHistoryModal();
  }

  updateRefreshButtonClicked(val: boolean): void {
    this._refreshButtonClicked$.next(val);
    if (this._storeQueryService.isStateDirty() || this._retailBridgeService.clear1HasUnsavedChanges) {
      this.openRefreshDialog();
    } else {
      window.location.reload();
    }
  }

  updateResetButtonClicked(val: boolean): void {
    this._resetButtonClicked$.next(val);
  }

  updateSaveButtonClicked(val: boolean): void {
    this._saveButtonClicked$.next(val);
  }

  setSelectedSlideoutPanelType(type: LoanStatusCardType | QuickActionDropdownType): void {
    this._slideOutPanel$.next(type);
  }

  private _initLoanStatusCardConfig() {
    const loanSummaryCard = LoanStatusCardConfig.find((t) => t.cardType === LoanStatusCardType.LoanSummary);
    const loanSummaryCardIndex = LoanStatusCardConfig.findIndex((t) => t.cardType === LoanStatusCardType.LoanSummary);
    const loanSummaryUpdated = Object.assign({}, loanSummaryCard);
    loanSummaryUpdated.navigation = 'loansummary';
    LoanStatusCardConfig[loanSummaryCardIndex] = loanSummaryUpdated;

    const creditCard = LoanStatusCardConfig.find((t) => t.cardType === LoanStatusCardType.Credit);
    const creditCardIndex = LoanStatusCardConfig.findIndex((t) => t.cardType === LoanStatusCardType.Credit);
    const creditUpdated = Object.assign({}, creditCard);
    LoanStatusCardConfig[creditCardIndex] = creditUpdated;

    this._sessionQuery
      .select((s) => s.all1003Loaded)
      .pipe(
        filter((all1003Loaded) => all1003Loaded === true),
        take(1),
      )
      .subscribe(() => {
        this._featureFlagsRepository
          .isFeatureEnabled$(FeatureName.CCW2)
          .pipe(take(1))
          .subscribe((isCCW2Enabled) => {
            // Fees tile is hidden when CCW2 flag is disabled
            if (isCCW2Enabled) {
              const ccwCard = LoanStatusCardConfig.find((t) => t.cardType === LoanStatusCardType.Fees);
              if (ccwCard) {
                const ccwUpdated = Object.assign({}, ccwCard);
                ccwUpdated.isHidden = false;
                ccwUpdated.navigation = './closing-cost-worksheet-2-0/fee-worksheet';
                const ccwCardIndex = LoanStatusCardConfig.findIndex((t) => t.cardType === LoanStatusCardType.Fees);
                if (ccwCardIndex > 0) {
                  LoanStatusCardConfig[ccwCardIndex] = ccwUpdated;
                }
              }
            }
          });

        const pricingCard = LoanStatusCardConfig.find((t) => t.cardType === LoanStatusCardType.Pricing);
        if (pricingCard) {
          const pricingUpdated = Object.assign({}, pricingCard);
          pricingUpdated.navigation = './pricing';
          const pricingCardIndex = LoanStatusCardConfig.findIndex((t) => t.cardType === LoanStatusCardType.Pricing);
          if (pricingCardIndex > 0) {
            LoanStatusCardConfig[pricingCardIndex] = pricingUpdated;
          }
        }
      });
  }

  private _initLoanBannerData(): void {
    const securityProfileStoreValue: any = this._securityProfileStore.getValue();
    this._activeProfile = securityProfileStoreValue?.entities[securityProfileStoreValue.active];
    this._initLoanStatusCardConfig();
    combineLatest([
      this._fileData$,
      this._loanInfo$,
      this._loanStatus$,
      this._transactionInfo$,
      this._borrowers$,
      this._income$,
      this._property$,
      this._lookups$,
      this._documentSummary$,
      this._creditSummary$,
      this._aus$,
      this._disclosures$,
      this._disclosureDates$,
      this._validations$,
      this._form4506cService.update$,
      this._form4506cService.irsForm4506$.pipe(catchError(() => of(null))),
      this._storeQueryService.isSandboxLoan$,
      this._appSettingsService.isShowSandboxLoanLpa$,
      this._featureFlagsRepository.isFeatureEnabled$(FeatureName.SandboxLoanMVP2),
      this._featureFlagsRepository.isFeatureEnabled$(FeatureName.CCW2),
      this._totalNriFromReo$,
    ])
      .pipe(
        distinctUntilChanged(),
        debounceTime(0),
        tap(
          ([
            fileData,
            loanInfo,
            loanStatus,
            transactionInfo,
            borrowers,
            income,
            property,
            lookups,
            documentSummary,
            creditSummary,
            aus,
            disclosuresLoan,
            disclosureDates,
            validations,
            form4506CUpdate,
            form4506Info,
            isSandboxLoan,
            isShowSandboxLoanLpa,
            isSandboxLoanMVP2Enabled,
            isCCW2Enabled,
            totalNriFromReo,
          ]) => {
            this._resetStores();
            const status = lookups.loanStatusItems?.find((s) => s.id === loanStatus?.toString());
            this._loanBannerDetailsStore.update({
              loanNumber: fileData?.fileName,
              status: status?.name,
              amount: loanInfo?.totalLoanAmount,
              cashToClose: transactionInfo?.cashToClose,
              interestRate: loanInfo?.interestRate / 100.0,
              firstRatio: loanInfo?.firstRatio / 100.0,
              secondRatio: loanInfo?.secondRatio / 100.0,
              ltv: loanInfo?.ltv,
              cltv: loanInfo?.cltv,
            });
            this._updateRESPA(borrowers, income, property, loanInfo, totalNriFromReo);
            this._updateCredit(creditSummary, borrowers);
            this._updatePricing(loanInfo);
            const isShowLoanLPA = isSandboxLoanMVP2Enabled || isShowSandboxLoanLpa;
            this._updateAUS(aus, loanInfo, validations, isShowLoanLPA, isSandboxLoan);
            this._updateDocs(documentSummary);
            this._updateDisclosures(disclosuresLoan, disclosureDates, status);
            this._updateForm4506C(form4506Info, form4506CUpdate);

            this._updateSandboxVisibility(isSandboxLoan, isSandboxLoanMVP2Enabled, isCCW2Enabled);
          },
        ),
      )
      .subscribe();

    this._disclosuresApplicationDate$
      .pipe(distinctUntilChanged((old, current) => old.every((value, index) => value === current[index])))
      .subscribe((value) => {
        const fileDataId = value[1];
        if (fileDataId) {
          this._loanApplicationService.getDisclosureDate(fileDataId).subscribe();
        }
      });
  }

  private _updateRESPA(
    borrowers: BorrowerModel[],
    employmentIncome: EmploymentIncomeExpenseModel[],
    property: PropertyInformationModel,
    loanInfo: LoanDataModel,
    totalNriFromReo?: number,
  ): void {
    const isFhaStreamlineOrVaIrrrl = isFhaRefiStreamlineVaRefiIRRRL(loanInfo);
    this.isFhaStreamlineOrVaIrrrl$.next(isFhaStreamlineOrVaIrrrl);
    const tridCompleteFields = getRESPACompleteFields(borrowers, employmentIncome, property, loanInfo, isFhaStreamlineOrVaIrrrl, totalNriFromReo);
    this.tridCompleteFields$.next(tridCompleteFields);
    const statusCard = getRESPAStatus(borrowers, employmentIncome, property, loanInfo, isFhaStreamlineOrVaIrrrl, totalNriFromReo);
    this._loanBannerStatusCardStore.update(LoanStatusCardType.TRID, statusCard);

    this._sessionQuery
      .select((s) => s.isRespaComplete)
      .pipe(take(1))
      .subscribe((isRespaComplete) => {
        if (isRespaComplete && tridCompleteFields.length !== respaRequiredFieldCount) {
          this._sessionStore.update({ isRespaComplete: false });
        } else if (!isRespaComplete && tridCompleteFields.length === respaRequiredFieldCount) {
          this._sessionStore.update({ isRespaComplete: true });
        }
      });

    if (tridCompleteFields?.length === respaRequiredFieldCount && !loanInfo?.applicationDate) {
      this._respaNoAppDateStatus$.next(RespaStatusType.Completed);
    } else if (
      isFhaStreamlineOrVaIrrrl &&
      tridCompleteFields?.length === 5 &&
      !tridCompleteFields?.includes(respaFields.Income) &&
      !loanInfo?.applicationDate
    ) {
      this._respaNoAppDateStatus$.next(RespaStatusType.FhaVaRefinanceCompleted);
    } else {
      this._respaNoAppDateStatus$.next(RespaStatusType.NotCompleted);
    }
  }

  private _updateForm4506C(form4506LoanModel: IrsForm4506LoanModel, update: IrsForm4506[] | null): void {
    const statusCard = GetForm4506CStatus(form4506LoanModel, update);
    this._loanBannerStatusCardStore.update(LoanStatusCardType.Form4506C, statusCard);
  }

  private _updateCredit(creditSummary: CreditSummaryModel, borrowers: BorrowerModel[]): void {
    const statusCard = GetCreditStatus(creditSummary, borrowers);
    this._loanBannerStatusCardStore.update(LoanStatusCardType.Credit, statusCard);
  }

  private _updatePricing(loanInfo: LoanDataModel): void {
    const statusCard = GetPricingStatus(loanInfo);
    this._loanBannerStatusCardStore.update(LoanStatusCardType.Pricing, statusCard);
  }

  private _updateAUS(
    aus: AusModel,
    loanInfo: LoanDataModel,
    validations: InvalidControlModel[],
    isShowSandboxLoanLpa: boolean,
    isSandboxLoan: boolean,
  ): void {
    const hasAusPanelBeenOpened = this._sessionStore.getValue().hasAusPanelBeenOpened;
    const statusCard = GetAusStatus(
      hasAusPanelBeenOpened,
      aus,
      loanInfo,
      validations,
      this._activeProfile.businessChannel,
      isSandboxLoan,
      isShowSandboxLoanLpa,
    );
    this._loanBannerStatusCardStore.update(LoanStatusCardType.AUS, statusCard);
  }

  private _updateDocs(documentSummary: DocumentSummaryModel): void {
    const statusCard = GetDocsStatus(documentSummary);
    this._loanBannerStatusCardStore.update(LoanStatusCardType.Docs, statusCard);
  }

  private _updateDisclosures(disclosuresLoan: LoanApplicationModel, disclosureDate: DisclosureDates, status: IdValue<string, string, string>): void {
    const cardBase = GetDisclosuresStatus(disclosuresLoan, disclosureDate, status);
    this._loanBannerStatusCardStore.update(LoanStatusCardType.Disclosures, { ...cardBase });
  }

  private _updateSandboxVisibility(isSandboxLoan: boolean, isSandboxLoanMVP2Enabled = false, isCCW2Enabled = false): void {
    if (isSandboxLoan) {
      this._loanBannerStatusCardStore.update(LoanStatusCardType.Disclosures, { isHidden: true });
      this._loanBannerStatusCardStore.update(LoanStatusCardType.Docs, { isHidden: true });
      this._loanBannerStatusCardStore.update(LoanStatusCardType.Form4506C, { isHidden: true });
      this._loanBannerStatusCardStore.update(LoanStatusCardType.AUS, { isHidden: !isSandboxLoanMVP2Enabled });
      this._loanBannerStatusCardStore.update(LoanStatusCardType.Fees, { isHidden: !(isSandboxLoanMVP2Enabled && isCCW2Enabled) });
    }
  }

  private _resetStores(): void {
    this._loanBannerStatusCardStore.reset();
    this._loanBannerStatusCardStore.add(LoanStatusCardConfig);
  }
}
